import { auditReportsExecuteGipReport } from '@mi-front/api-v2';
import { ViewLoader } from '@ui/utils';
import { useIntl } from 'react-intl';
import { useClient } from '../../../../../../../../core/auth/profile/hooks/useClient';
import { useTimezone } from '../../../../../../../../core/hooks/useTimezone';
import { ReportView } from '../../../../../../../../core/reports/components/ReportView';
import { useStationsWithAudit } from '../../../../../../hooks/useStationsWithAudit';
import { GipReportFormActions } from '../config/form/components/ReportActions';
import { GipReportForm } from '../config/form/GipReportForm';
import { useGipQueryParamsTransform } from '../config/form/hooks/useGipQueryParamsTransform';
import { useGipReportDefaultValues } from '../config/form/hooks/useGipReportDefaultValues';
import { GipReportType } from '../config/form/types';
import {
  gipReportDateRangeExceededMessage,
  gipReportFromMustBeBeforeToMessage,
} from '../config/gip-report-validation-messages';
import {
  assertGipReportDateRange,
  GIP_REPORT_MAX_DATE_RANGE_DAYS,
} from '../config/utils/assert-gip-report-date-range';
import { mapValuesToGipReportDataDto } from '../config/utils/gip-report-values-to-data-dto';
import { GipReportResultV2 } from './components/result/GipReportResultV2';
import { useGipValuesFromReportId } from './hooks/useGipValuesFromState';

type Props = Record<string, never>;

export function GipReportPreviewView(_props: Props) {
  const stations = useStationsWithAudit();
  const client = useClient();
  const intl = useIntl();
  const tz = useTimezone();

  const defaultValues = useGipReportDefaultValues();
  const { values: passedValues, isLoading } = useGipValuesFromReportId();

  const paramsTransform = useGipQueryParamsTransform();

  if (isLoading) {
    return <ViewLoader />;
  }

  return (
    <ReportView<GipReportType>
      download={{
        local: false,
        filename: 'gip-report',
      }}
      runOnLoad={!!passedValues}
      defaultValues={passedValues ?? defaultValues}
      queryParamsTransform={paramsTransform}
      reportFn={async (values, format) => {
        assertGipReportDateRange(
          values,
          GIP_REPORT_MAX_DATE_RANGE_DAYS,
          intl.formatMessage(gipReportDateRangeExceededMessage, {
            maxDays: GIP_REPORT_MAX_DATE_RANGE_DAYS,
          }),
          intl.formatMessage(gipReportFromMustBeBeforeToMessage),
        );

        const config = mapValuesToGipReportDataDto(client.id, values, stations, tz);

        // Always use the modern API - supports both preview and download with format parameter
        return auditReportsExecuteGipReport(
          config,
          format ? { format } : undefined,
          format ? { responseType: 'arraybuffer' as const } : undefined,
        ) as unknown as GipReportType['Result'];
      }}
      Form={GipReportForm}
      FormActions={GipReportFormActions}
      Result={GipReportResultV2}
    />
  );
}
